import React, { useCallback, useReducer, useState } from "react";
import styled from "styled-components";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { FormConfiguration } from "../FormConfiguration";
import { getFormConfig } from "../Testing/FormTestData";
import { FormRuntime } from "../FormRuntime";
import { XColumnContainer, XColumnContainerColumn, XStackContainer } from "../Containers";
import { HStackAlignment, HStackVerticalAlignment, XHStackContainer } from "../Containers/XHStackContainer";
import { TextFormControl } from "../Controls/Web/TextFormControl";
import { ButtonFormControl } from "../Controls/Web/ButtonFormControl";
import { DefaultOnOff } from "../Utilities/DefaultOnOff";
import { MultiLineTextEditFormControl } from "../Controls/Web/MultiLineTextEditFormControl";
import { SelectFormControl } from "../Controls/Web/SelectFormControl";
import { CheckboxFormControl } from "../Controls/Web/CheckboxFormControl";
import { ExecutionData } from "../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { InputLabel } from "@mui/material";
import { BorderStyle, TextControlSize, TextControlStyle } from "../Utilities/FormDesignConstants";
import { StoryNode } from "../../CommonUI/StoryBook/StoryData";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export interface XFormDesignerProps {
    form: FormConfiguration;
}

interface StoryHostXUserFormStyleProps {
    width?: number;
    height?: number;
    background?: string;
    border?: string;
}

// --------------------------------
// control for testing
// --------------------------------

const StoryHostWrapper = styled("div") <{ background?: string }>`
    display: flex;
    flex-direction: row;
    background: ${(props) => props.background || UIStyleConstants.GRAY_LIGHT_COLOR};
`;

const XUserFormWrapper = styled("div") <StoryHostXUserFormStyleProps & { hasHeader?: boolean }>`
    height: ${(props) => (props.height ? `${props.height}px` : "auto")};
    width: ${(props) => (props.width ? `${props.width}px` : "100%")};
    overflow: visible;
    border: ${(props) => props.border || "solid 1px gray"};
    background: ${UIStyleConstants.WHITE_COLOR};
    margin: ${(props) => (props.hasHeader ? "83px 33px 33px" : "33px")};
    position: relative;
`;

const InfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin: 33px;
`;

interface StoryHostXUserFormProps extends XFormDesignerProps, StoryHostXUserFormStyleProps {
    formName?: string;
    showCancel?: boolean;
    formTitle?: string;
    onFormClose?: (data: ExecutionData) => void;
}

const initialState: string[] = [];

enum EventLogType {
    ADD_EVENT = "ADD_EVENT",
    REMOVE_EVENT = "REMOVE_EVENT",
}

const eventLogsReducer = (state: string[], action: { type: EventLogType; logMessage?: string; index?: number }) => {
    switch (action.type) {
        case EventLogType.ADD_EVENT:
            return [...state, action.logMessage || ""];
        case EventLogType.REMOVE_EVENT:
            return state.splice(action.index || 0, 1);
    }
};

function StoryHostXUserForm(props: { storyHostProps: StoryHostXUserFormProps }) {
    const [formData, setFormData] = useState<ExecutionData | null>(null);
    const [eventLogs, dispatch] = useReducer(eventLogsReducer, initialState);

    const addEventLog = useCallback(
        (logMessage: string) => {
            dispatch({
                type: EventLogType.ADD_EVENT,
                logMessage,
            });
        },
        [dispatch],
    );

    const onFormDataChange = useCallback(
        (data: ExecutionData) => {
            setFormData(data);
            addEventLog("Form Data is changed");
        },
        [setFormData, addEventLog],
    );

    const onFormButtonClick = useCallback(
        (buttonName: string, _data: ExecutionData) => {
            addEventLog(`Button ${buttonName}: clicked`);
        },
        [addEventLog],
    );

    const onFormCancelButton = useCallback(() => {
        addEventLog(`Cancel Form is triggered`);
    }, [addEventLog]);

    const onFormClose = useCallback(
        (data: ExecutionData) => {
            props.storyHostProps.onFormClose!(data);
            setFormData(data);
            addEventLog("Close Form event is triggered");
        },
        [addEventLog, props.storyHostProps.onFormClose],
    );

    if (props.storyHostProps.width) {
        props.storyHostProps.form.runtimeWidth = props.storyHostProps.width;
    }

    return (
        <StoryHostWrapper background={props.storyHostProps.background}>
            <XUserFormWrapper
                height={props.storyHostProps.height}
                width={props.storyHostProps.width}
                border={props.storyHostProps.border}
                hasHeader={!!props.storyHostProps.formTitle || !!props.storyHostProps.onFormClose}
            >
                <div style={{ width: "100%", height: "100%", overflow: "auto" }}>
                    <FormRuntime
                        form={props.storyHostProps.form}
                        formTitle={props.storyHostProps.formTitle}
                        onFormDataChange={onFormDataChange}
                        onFormButtonClick={onFormButtonClick}
                        onFormCancelButton={onFormCancelButton}
                        onFormClose={props.storyHostProps.onFormClose ? onFormClose : undefined}
                    />
                </div>
            </XUserFormWrapper>
            <InfoWrapper>
                <div>
                    <InputLabel>Form Data</InputLabel>
                    <TextareaAutosize
                        minRows={3}
                        value={formData ? JSON.stringify(formData, null, 2) : ""}
                    />
                </div>
                <div>
                    <InputLabel>Event Logs</InputLabel>
                    <TextareaAutosize
                        minRows={3}
                        value={eventLogs.join("\n")}
                    />
                </div>
            </InfoWrapper>
        </StoryHostWrapper>
    );
}

export const FormUserStories = [
    new StoryNode(
        () => {
            let form = getFormConfig();
            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "All Controls",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();
            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Validation Breaks -- MISSING",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();

            form = new FormConfiguration();
            let cContainer = new XColumnContainer();
            let hContainer = new XHStackContainer();
            let sContainer = new XStackContainer();

            form.add(cContainer);
            form.add(hContainer);
            form.add(sContainer);

            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "No Controls",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();

            form = new FormConfiguration();
            let columnContainer = new XColumnContainer();
            let actualCol = new XColumnContainerColumn();
            let actualCol2 = new XColumnContainerColumn();
            let actualCol3 = new XColumnContainerColumn();
            actualCol3.overrideFormBorderSettings = DefaultOnOff.On;
            actualCol3.columnBorderWidth = 3;
            actualCol3.columnBorderColor = UIStyleConstants.RED_COLOR;

            actualCol.targetWidth = 30;
            actualCol.minSizePx = 100;
            actualCol2.targetWidth = 70;
            actualCol2.minSizePx = 300;
            actualCol3.targetWidth = 20;
            actualCol3.minSizePx = 100;

            // show border so we can see
            columnContainer.overrideFormBorderSettings = DefaultOnOff.On;
            columnContainer.showContainerBorder = true;
            columnContainer.defaultShowColumnBorder = true;

            columnContainer.add(actualCol);
            columnContainer.add(actualCol2);
            columnContainer.add(actualCol3);

            actualCol.add(new TextFormControl());
            actualCol.add(new TextFormControl());
            actualCol.add(new TextFormControl());
            actualCol2.add(new TextFormControl());
            actualCol2.add(new TextFormControl());
            actualCol2.add(new TextFormControl());

            let hstackContainer = new XHStackContainer();
            hstackContainer.verticalAlignment = HStackVerticalAlignment.MIDDLE;
            hstackContainer.overrideFormBorderSettings = DefaultOnOff.On;
            hstackContainer.showContainerBorder = true;

            let button = new ButtonFormControl();
            button.label = "Submit";
            let button2 = new ButtonFormControl();
            button.label = "Cancel";
            hstackContainer.add(new TextFormControl());
            hstackContainer.add(new TextFormControl());
            hstackContainer.add(button);
            hstackContainer.add(button2);

            form.add(columnContainer);
            form.add(hstackContainer);

            let p: StoryHostXUserFormProps = {
                form: form,
                width: 375,
                height: 667,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Responsive Thin Form",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();
            form = new FormConfiguration();
            let columnContainer = new XColumnContainer();
            let actualCol = new XColumnContainerColumn();
            let actualCol2 = new XColumnContainerColumn();
            let actualCol3 = new XColumnContainerColumn();

            actualCol.targetWidth = 30;
            actualCol.minSizePx = 100;
            actualCol2.targetWidth = 70;
            actualCol2.minSizePx = 300;
            actualCol3.targetWidth = 20;
            actualCol3.minSizePx = 100;

            columnContainer.add(actualCol);
            columnContainer.add(actualCol2);
            columnContainer.add(actualCol3);

            columnContainer.overrideFormBorderSettings = DefaultOnOff.On;
            columnContainer.showContainerBorder = true;
            columnContainer.defaultShowColumnBorder = true;

            columnContainer.lineBetweenColumns = true;
            columnContainer.lineWidthBetweenColumns = 2;
            columnContainer.lineColorBetweenColumns = "gray";

            form.add(columnContainer);

            let p: StoryHostXUserFormProps = {
                form: form,
                width: 375,
                height: 667,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Resonsive Thin Line Between Containers",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();
            form = new FormConfiguration();
            let columnContainer = new XColumnContainer();

            let actualCol = new XColumnContainerColumn();
            let actualCol2 = new XColumnContainerColumn();
            let actualCol3 = new XColumnContainerColumn();

            columnContainer.add(actualCol);
            columnContainer.add(actualCol2);
            columnContainer.add(actualCol3);

            actualCol.targetWidth = 30;
            actualCol.minSizePx = 500;
            actualCol2.targetWidth = 70;
            actualCol2.minSizePx = 300;
            actualCol3.targetWidth = 20;
            actualCol3.minSizePx = 100;

            columnContainer.lineBetweenColumns = true;
            columnContainer.lineWidthBetweenColumns = 2;
            columnContainer.lineColorBetweenColumns = "gray";

            form.add(columnContainer);
            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Lines Between With Overlap Column",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();
            form.doNotScrollLastContainerOnForm = true;
            form.doNotScrollFirstContainerOnForm = true;
            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Pinned Sections",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();
            form.doNotScrollLastContainerOnForm = true;
            form.doNotScrollFirstContainerOnForm = true;

            let onFormClose = (_data: ExecutionData) => { };

            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
                formTitle: "Test Form",
                onFormClose: onFormClose,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Pinned Sections With Header",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();
            form.doNotScrollLastContainerOnForm = true;
            form.doNotScrollFirstContainerOnForm = true;

            let onFormClose = (_data: ExecutionData) => { };

            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
                formTitle: "Test Form",
                onFormClose: onFormClose,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Pinned Sections With Header",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();
            form = new FormConfiguration();
            let stackContainer = new XStackContainer();
            let textField = new TextFormControl();

            textField.valueName = "textfield";
            textField.label = "Text Field";

            let textareaField = new MultiLineTextEditFormControl();
            textareaField.valueName = "textarea";
            textareaField.label = "Textarea Field";

            let selectField = new SelectFormControl();
            selectField.valueName = "selectbox";
            selectField.list = ["Option 1", "Option 2"];

            let checkboxField = new CheckboxFormControl();
            checkboxField.valueName = "checkbox";

            stackContainer.add(textField);
            stackContainer.add(textareaField);
            stackContainer.add(selectField);
            stackContainer.add(checkboxField);

            form.add(stackContainer);

            let formStorageData = form.getStorageData();
            let s = JSON.stringify(formStorageData, null, 2);

            let sasobj = JSON.parse(s);
            let newForm = new FormConfiguration();
            newForm.setStorageData(sasobj);

            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Data Changed Externally ?? WRONG",
    ),

    new StoryNode(
        () => {
            let form = getFormConfig();

            form.designFormWidth = 500;

            form.runtimeWidth = 600;
            form.gapBetweenContainers = 10;
            form.formMargin = 5;
            form.defaultInnerContainerMargin = 8;
            form.defaultInnerColumnMargin = 2;
            form.defaultInterControlSpacing = 8;

            // // first container
            form.doNotScrollLastContainerOnForm = true;
            form.doNotScrollFirstContainerOnForm = false;

            form.defaultShowContainerBorder = true;
            form.defaultContainerBorderRadius = 10;
            form.defaultContainerBorderWidth = 3;
            form.defaultContainerBorderColor = UIStyleConstants.GREEN_COLOR;
            form.defaultContainerBorderStyle = BorderStyle.Groove;

            form.defaultLineBetweenContainerWidth = 2;
            form.showLinesBetweenContainers = true;
            form.defaultLineBetweenContainerColor = UIStyleConstants.BLACK_COLOR;
            form.defaultLineBetweenContainerStyle = BorderStyle.Dashed;

            form.formBackgroundColor = UIStyleConstants.GRAY_COLOR;
            form.defaultContainerBackgroundColor = UIStyleConstants.GRAY_COLOR;

            form.defaultTextStyle = TextControlStyle.OUTLINE;
            form.defaultTextSize = TextControlSize.SMALL;

            let containers = form.getContainers();

            let hstackContainer = containers[3] as XHStackContainer;
            hstackContainer.alignment = HStackAlignment.CENTER;
            hstackContainer.verticalAlignment = HStackVerticalAlignment.MIDDLE;

            let stackContainer = containers[1] as XStackContainer;
            stackContainer.interControlSpacing = 20;

            let stackContainer1 = containers[0] as XStackContainer;
            stackContainer1.containerBackgroundColor = UIStyleConstants.BLACK_COLOR;

            let columnContainer = containers[2] as XColumnContainer;

            columnContainer.overrideFormBorderSettings = DefaultOnOff.On;
            columnContainer.defaultShowColumnBorder = true;
            columnContainer.defaultColumnBorderColor = UIStyleConstants.RED_COLOR;
            columnContainer.defaultInnerColumnMargin = 10;

            columnContainer.lineBetweenColumns = true;
            columnContainer.lineWidthBetweenColumns = 1;
            columnContainer.lineColorBetweenColumns = UIStyleConstants.BLUE_COLOR;

            let formStorageData = form.getStorageData();
            let s = JSON.stringify(formStorageData, null, 2);

            let sasobj = JSON.parse(s);
            let newForm = new FormConfiguration();
            newForm.setStorageData(sasobj);

            let p: StoryHostXUserFormProps = {
                form: newForm,
                width: 800,
                height: 800,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Data Read In From JSON",
    ),

    new StoryNode(
        () => {
            let form = new FormConfiguration();
            let stack = new XStackContainer();
            form.add(stack);
            let control = new TextFormControl();
            control.valueName = "test";
            control.label = "test label";
            stack.add(control);
            let p: StoryHostXUserFormProps = {
                form: form,
                width: 800,
                height: 800,
            };

            return <StoryHostXUserForm storyHostProps={p}></StoryHostXUserForm>;
        },

        "Form Builder",
        "Runtime",
        "Simple Form",
    ),
];
